import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Space,
  Badge,
  MenuProps,
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LockOutlined,
  ExperimentOutlined,
  CloudServerOutlined,
  FolderOpenOutlined,
  BookOutlined,
  NotificationOutlined,
  PartitionOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  TrophyOutlined,
  TagsOutlined,
  WalletOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  GiftOutlined,
  CloudDownloadOutlined,
  CreditCardOutlined,
  DatabaseOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [username, setUsername] = useState<string>('管理员');
  const navigate = useNavigate();
  const location = useLocation();

  // 从 localStorage 获取用户信息
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setUsername(user.nickname || user.email || '管理员');
      } catch (e) {
        console.error('解析用户信息失败');
      }
    }
  }, []);

  // 侧边栏菜单配置
  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: '数据看板',
      children: [
        {
          key: '/dashboard',
          label: '概览',
        },
        {
          key: '/analytics/events',
          icon: <BarChartOutlined />,
          label: '埋点数据',
        },
        {
          key: '/operations/overview',
          icon: <SafetyCertificateOutlined />,
          label: '运营数据',
        },
      ],
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: '创作者管理',
    },
    {
      key: 'data-reporting',
      icon: <CloudUploadOutlined />,
      label: '数据上报',
      children: [
        {
          key: '/data-reporting',
          icon: <CloudUploadOutlined />,
          label: '北极星数据上报',
        },
        {
          key: '/luming-reporting',
          icon: <CloudUploadOutlined />,
          label: '鹿鸣数据上报',
        },
      ],
    },
    {
      key: 'ink',
      icon: <WalletOutlined />,
      label: '墨水管理',
      children: [
        {
          key: '/ink/accounts',
          icon: <WalletOutlined />,
          label: '账户管理',
        },
        {
          key: '/ink/transfers',
          icon: <RiseOutlined />,
          label: '后台划转',
        },
        {
          key: '/ink/records',
          icon: <BookOutlined />,
          label: '流水记录',
        },
        {
          key: '/operations/orders',
          icon: <ShoppingCartOutlined />,
          label: '订单管理',
        },
        {
          key: '/finance/refunds',
          icon: <WalletOutlined />,
          label: '退款审核',
        },
        {
          key: '/payment-management',
          icon: <CreditCardOutlined />,
          label: '支付配置',
        },
      ],
    },
    {
      key: 'content',
      icon: <BookOutlined />,
      label: '内容运营',
      children: [
        {
          key: '/content/books',
          icon: <BookOutlined />,
          label: '书阁管理',
        },
        {
          key: '/content/authors',
          icon: <UserOutlined />,
          label: '作家管理',
        },
        {
          key: '/content/categories',
          icon: <TagsOutlined />,
          label: '分类管理',
        },
        {
          key: '/content/sections',
          icon: <PartitionOutlined />,
          label: '书阁版块',
        },
        {
          key: '/content/rankings',
          icon: <TrophyOutlined />,
          label: '巅峰榜管理',
        },
        {
          key: '/content/news',
          icon: <NotificationOutlined />,
          label: '最新资讯',
        },
        {
          key: '/content/bookshelves',
          icon: <BookOutlined />,
          label: '用户书架',
        },
        {
          key: '/creation/workflows',
          icon: <PartitionOutlined />,
          label: '流程监管',
        },
        {
          key: '/operations/campaigns',
          icon: <GiftOutlined />,
          label: '活动配置',
        },
      ],
    },
    {
      key: 'ai-management',
      icon: <ExperimentOutlined />,
      label: 'AI 管理',
      children: [
        {
          key: '/ai-management/config',
          label: 'AI配置',
        },
        {
          key: '/ai-management/character-names',
          label: '人物名字库',
        },
        {
          key: '/ai-management/costs',
          label: '费用监控',
        },
      ],
    },
    {
      key: 'ai-tests',
      icon: <ExperimentOutlined />,
      label: 'AI测试管理',
      children: [
        {
          key: '/ai-tests/original',
          label: '原平台AI测试',
        },
        {
          key: '/ai-tests/image-generation',
          label: '织梦文生图测试',
        },
      ],
    },
    {
      key: 'auth',
      icon: <LockOutlined />,
      label: '权限管理',
      children: [
        {
          key: '/roles',
          label: '角色管理',
        },
        {
          key: '/permissions',
          label: '权限列表',
        },
      ],
    },
    {
      key: 'resource',
      icon: <CloudServerOutlined />,
      label: '资源管理',
      children: [
        {
          key: '/resource/list',
          icon: <FolderOpenOutlined />,
          label: '资源列表',
        },
        {
          key: '/resource/pull',
          icon: <CloudDownloadOutlined />,
          label: '资源抓取',
        },
        {
          key: '/resource/storage',
          icon: <CloudServerOutlined />,
          label: '云存储配置',
        },
        {
          key: '/initialization',
          icon: <DatabaseOutlined />,
          label: '初始化数据',
        },
      ],
    },
    {
      key: 'system',
      icon: <SettingOutlined />,
      label: '系统管理',
      children: [
        {
          key: '/system/admins',
          label: '管理员管理',
        },
        {
          key: '/system/content-studio',
          icon: <CloudServerOutlined />,
          label: '内容中台配置',
        },
        {
          key: '/settings',
          label: '系统设置',
        },
      ],
    },
  ];

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: '个人中心',
      icon: <UserOutlined />,
    },
    {
      key: 'settings',
      label: '账户设置',
      icon: <SettingOutlined />,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = `${process.env.PUBLIC_URL || ''}/login`;
    } else if (key === 'profile') {
      console.log('个人中心');
    } else if (key === 'settings') {
      navigate('/settings');
    }
  };

  return (
    <Layout className="main-layout">
      {/* 侧边栏 */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={220}
        className="layout-sider"
      >
        <div className="logo">
          <UserOutlined className="logo-icon" />
          {!collapsed && <span className="logo-text">灵笔管理后台</span>}
        </div>
        <div className="layout-menu-scroll">
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </div>
      </Sider>

      <Layout>
        {/* 顶部导航栏 */}
        <Header className="layout-header">
          <div className="header-left">
            {React.createElement(
              collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
              {
                className: 'trigger',
                onClick: () => setCollapsed(!collapsed),
              }
            )}
          </div>

          <div className="header-right">
            <Space size="large">
              {/* 通知 */}
              <Badge count={5} size="small">
                <BellOutlined className="header-icon" />
              </Badge>

              {/* 用户信息 */}
              <Dropdown
                menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                placement="bottomRight"
              >
                <Space className="user-info">
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span className="username">{username}</span>
                </Space>
              </Dropdown>
            </Space>
          </div>
        </Header>

        {/* 内容区域 */}
        <Content className="layout-content">
          <div className="content-wrapper">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
